import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom"; // ✅ Naya import
import {
  FiPieChart,
  FiUsers,
  FiUserCheck,
  FiAward,
  FiDollarSign,
  FiTrendingDown,
  FiStar,
  FiActivity,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiList,
} from "react-icons/fi";
import {
  FaDragon,
  FaCat,
  FaBalanceScale,
  FaWallet,
  FaCoins,
  FaGamepad,
  FaPlane,
} from "react-icons/fa";
import { Admindashboard } from "../api/endpoints";
import AnimatedNumber from "../components/AnimatedNumber";
import PageHeader from "../components/PageHeader";

// ============================================================
// CONFIG
// ============================================================
const GAME_META = {
  DRAGON_TIGER: {
    label: "Dragon Tiger",
    accentColor: "#DC2626",
    accentBg: "#FEF2F2",
  },
  AVIATOR: {
    label: "Aviator",
    accentColor: "#0891B2",
    accentBg: "#ECFEFF",
  },
};

const FULL_REFRESH_INTERVAL = 30000; // 30 sec
const CLIENT_PAGE_SIZE = 10;

const Dashboard = () => {
  const navigate = useNavigate(); // ✅ Hook initialize kiya

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // ✅ Client-side pagination state (alag-alag per game)
  const [dtPage, setDtPage] = useState(1);
  const [avPage, setAvPage] = useState(1);

  const [activeBetTab, setActiveBetTab] = useState("DRAGON_TIGER");

  const currentPageRef = useRef(1);

  // ============================================================
  // FETCH — backend se saara data (bada limit)
  // ============================================================
  const fetchDashboardData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      const response = await Admindashboard({ page: 1, limit: 500 });

      if (response.success) {
        setDashboardData(response.data);
        setLastUpdated(new Date());
      } else if (!silent) {
        setError("API returned unsuccessful response");
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      
      // ✅ Token expire / Unauthorized check
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.clear();
        navigate("/login", { replace: true });
        return;
      }

      if (!silent) setError("Failed to load dashboard data");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // ============================================================
  // EFFECTS
  // ============================================================
  useEffect(() => {
    fetchDashboardData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData(true);
    }, FULL_REFRESH_INTERVAL);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================
  // HANDLERS
  // ============================================================
  const handleManualRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData(false);
    setTimeout(() => setRefreshing(false), 400);
  };

  // ============================================================
  // FORMATTERS
  // ============================================================
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "₹ 0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const formatTime = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "BETTING":
        return "bg-success";
      case "WIN":
      case "CASHED_OUT":
      case "SETTLED":
        return "bg-success";
      case "LOSS":
      case "LOST":
      case "CRASHED":
        return "bg-danger";
      case "PENDING":
        return "bg-warning";
      case "FLYING":
        return "bg-info";
      default:
        return "bg-info";
    }
  };

  const getSelectionBadgeColor = (selection) => {
    switch (selection) {
      case "DRAGON":
        return "bg-danger";
      case "TIGER":
        return "bg-info";
      case "TIE":
        return "bg-warning text-white";
      default:
        return "bg-secondary";
    }
  };

  // ============================================================
  // SAFE EXTRACTION (moved BEFORE hooks that use them)
  // ============================================================
  const recentBets = dashboardData?.recentBets || {};
  const dtBetsAll = recentBets.dragonTiger?.bets || [];
  const avBetsAll = recentBets.aviator?.bets || [];

  // ✅ Client-side pagination — DT
  const dtTotal = dtBetsAll.length;
  const dtTotalPages = Math.max(1, Math.ceil(dtTotal / CLIENT_PAGE_SIZE));
  const dtBets = useMemo(() => {
    const start = (dtPage - 1) * CLIENT_PAGE_SIZE;
    return dtBetsAll.slice(start, start + CLIENT_PAGE_SIZE);
  }, [dtBetsAll, dtPage]);

  // ✅ Client-side pagination — Aviator
  const avTotal = avBetsAll.length;
  const avTotalPages = Math.max(1, Math.ceil(avTotal / CLIENT_PAGE_SIZE));
  const avBets = useMemo(() => {
    const start = (avPage - 1) * CLIENT_PAGE_SIZE;
    return avBetsAll.slice(start, start + CLIENT_PAGE_SIZE);
  }, [avBetsAll, avPage]);

  // ============================================================
  // LOADING / ERROR
  // ============================================================
  if (loading && !dashboardData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="alert alert-danger m-4">
        <h4>Error Loading Dashboard</h4>
        <p>{error}</p>
        <button
          className="btn btn-primary"
          onClick={() => fetchDashboardData(false)}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="alert alert-info m-4">No dashboard data available</div>
    );
  }

  // ============================================================
  // SAFE EXTRACTION (rest)
  // ============================================================
  const users = dashboardData.users || {
    total: 0,
    today: 0,
    active: 0,
    inactive: 0,
  };
  const kyc = dashboardData.kyc || { verified: 0, pending: 0, rejected: 0 };
  const wallet = dashboardData.wallet || {
    totalBalance: 0,
    depositBalance: 0,
    winningBalance: 0,
    bonusBalance: 0,
  };
  const deposit = dashboardData.deposit || {
    pending: 0,
    approved: 0,
    todayAmount: 0,
  };
  const withdraw = dashboardData.withdraw || {
    pending: 0,
    approved: 0,
    todayAmount: 0,
  };
  const bet = dashboardData.bet || {
    combined: {},
    dragonTiger: {},
    aviator: {},
  };

  const dtRound = dashboardData.round?.dragonTiger;
  const avRound = dashboardData.round?.aviator;

  const statCards = [
    {
      title: "Total Users",
      rawValue: users.total,
      icon: <FiUsers className="fs-2" />,
      color: "primary",
      subtext: `${users.today || 0} new today`,
      progress: users.total > 0 ? ((users.active || 0) / users.total) * 100 : 0,
    },
    {
      title: "Active Users",
      rawValue: users.active || 0,
      icon: <FiUserCheck className="fs-2" />,
      color: "success",
      subtext: `${users.inactive || 0} inactive`,
      progress: users.total > 0 ? ((users.active || 0) / users.total) * 100 : 0,
    },
    {
      title: "Total Wallet Balance",
      rawValue: wallet.totalBalance || 0,
      formatter: formatCurrency,
      icon: <FaWallet className="fs-2" />,
      color: "warning",
      subtext: `Deposits: ${formatCurrency(wallet.depositBalance)}`,
      progress: 100,
    },
  ];

  return (
    <>
      <PageHeader
        icon={<FiPieChart />}
        title="Dashboard"
        subtitle="Live view of players, balances and game activity."
        actions={
          <>
            <span
              className="dashboard-live"
              title={
                lastUpdated ? `Last update ${formatTime(lastUpdated)}` : "Live"
              }
            >
              <span className="dashboard-live__dot" aria-hidden="true" />
              Updated {lastUpdated ? formatTime(lastUpdated) : "just now"}
            </span>

            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={handleManualRefresh}
              disabled={refreshing}
            >
              <FiRefreshCw className={`me-1 ${refreshing ? "spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </>
        }
      />

      <div className="app-body">
        {/* ===================== STAT CARDS ===================== */}
        <div className="row g-4 mb-4">
          {statCards.map((card, index) => (
            // ✅ col-xl-4 se 3 box bade ho jayenge
            <div className="col-xl-4 col-lg-6 col-md-6 col-12" key={index}>
              <div className="card stat-card border-0 shadow-sm h-100 hover-shadow transition">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <p className="text-muted small fw-semibold text-uppercase mb-1">
                        {card.title}
                      </p>
                      <h3 className="titleamt fw-medium">
                        <AnimatedNumber
                          value={card.rawValue}
                          formatter={card.formatter}
                        />
                      </h3>
                    </div>
                    <div
                      className={`icon-box sm rounded-3 bg-${card.color} bg-opacity-10 text-${card.color}`}
                    >
                      {card.icon}
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small text-muted">{card.subtext}</span>
                    <span className="small fw-semibold">
                      {Math.round(card.progress)}%
                    </span>
                  </div>
                  <div className="progress mt-2" style={{ height: "4px" }}>
                    <div
                      className={`progress-bar bg-${card.color}`}
                      style={{ width: `${card.progress}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ===================== WALLET BREAKDOWN ===================== */}
        <div className="row g-4 mb-4">
          <div className="col-xl-3 col-lg-6 col-md-6 col-12">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted small fw-semibold text-uppercase mb-1">
                      Deposit Balance
                    </p>
                    <h4 className="fw-bold mb-0 text-primary">
                      <AnimatedNumber
                        value={wallet.depositBalance || 0}
                        formatter={formatCurrency}
                      />
                    </h4>
                  </div>
                  <div className="icon-box sm rounded-3 bg-primary bg-opacity-10 text-primary">
                    <FaCoins />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-lg-6 col-md-6 col-12">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted small fw-semibold text-uppercase mb-1">
                      Winning Balance
                    </p>
                    <h4 className="fw-bold mb-0 text-success">
                      <AnimatedNumber
                        value={wallet.winningBalance || 0}
                        formatter={formatCurrency}
                      />
                    </h4>
                  </div>
                  <div className="icon-box sm rounded-3 bg-success bg-opacity-10 text-success">
                    <FiAward />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-lg-6 col-md-6 col-12">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted small fw-semibold text-uppercase mb-1">
                      Bonus Balance
                    </p>
                    <h4 className="fw-bold mb-0 text-warning">
                      <AnimatedNumber
                        value={wallet.bonusBalance || 0}
                        formatter={formatCurrency}
                      />
                    </h4>
                  </div>
                  <div className="icon-box sm rounded-3 bg-warning bg-opacity-10 text-warning">
                    <FiStar />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-lg-6 col-md-6 col-12">
            <div className="card border-0 shadow-sm h-100 bg-gradient-primary text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-white-50 small fw-semibold text-uppercase mb-1">
                      Total Balance
                    </p>
                    <h4 className="fw-bold mb-0">
                      <AnimatedNumber
                        value={wallet.totalBalance || 0}
                        formatter={formatCurrency}
                      />
                    </h4>
                  </div>
                  <div className="icon-box sm rounded-3 bg-white bg-opacity-20 text-white">
                    <FaWallet />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===================== DEPOSITS & WITHDRAWALS ===================== */}
        <div className="row g-4 mb-4">
          <div className="col-xl-6 col-lg-6 col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0 fw-bold">
                  <FiDollarSign className="me-2 text-success" /> Deposits
                </h5>
                <span className="badge bg-success bg-opacity-10 text-success">
                  {deposit.pending || 0} pending
                </span>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-6">
                    <div className="text-center p-3 bg-light rounded-3">
                      <h6 className="text-muted small mb-1">Approved</h6>
                      <h3 className="fw-bold text-success mb-0">
                        <AnimatedNumber value={deposit.approved || 0} />
                      </h3>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-3 bg-light rounded-3">
                      <h6 className="text-muted small mb-1">Pending</h6>
                      <h3 className="fw-bold text-warning mb-0">
                        <AnimatedNumber value={deposit.pending || 0} />
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="mt-3 d-flex justify-content-between align-items-center">
                  <span className="text-muted">Today's Amount</span>
                  <span className="fw-bold text-success">
                    <AnimatedNumber
                      value={deposit.todayAmount || 0}
                      formatter={formatCurrency}
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-6 col-lg-6 col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0 fw-bold">
                  <FiTrendingDown className="me-2 text-danger" /> Withdrawals
                </h5>
                <span className="badge bg-danger bg-opacity-10 text-danger">
                  {withdraw.pending || 0} pending
                </span>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-6">
                    <div className="text-center p-3 bg-light rounded-3">
                      <h6 className="text-muted small mb-1">Approved</h6>
                      <h3 className="fw-bold text-success mb-0">
                        <AnimatedNumber value={withdraw.approved || 0} />
                      </h3>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-3 bg-light rounded-3">
                      <h6 className="text-muted small mb-1">Pending</h6>
                      <h3 className="fw-bold text-warning mb-0">
                        <AnimatedNumber value={withdraw.pending || 0} />
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="mt-3 d-flex justify-content-between align-items-center">
                  <span className="text-muted">Today's Amount</span>
                  <span className="fw-bold text-danger">
                    <AnimatedNumber
                      value={withdraw.todayAmount || 0}
                      formatter={formatCurrency}
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===================== LIVE GAME ROUNDS ===================== */}
        <div className="mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fw-bold mb-0">
              <FaGamepad className="me-2 text-primary" />
              Live Game Rounds
            </h5>
            <span
              className="badge bg-success bg-opacity-10 text-success"
              style={{ fontSize: "0.65rem" }}
            >
              ● Live · updates every 30s
            </span>
          </div>
          <small className="text-muted">Real-time round status</small>
        </div>

        <div className="row g-4 mb-4">
          {/* Dragon Tiger */}
          <div className="col-xl-6 col-lg-6 col-12">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3"
                    style={{
                      width: 40,
                      height: 40,
                      background: GAME_META.DRAGON_TIGER.accentBg,
                      color: GAME_META.DRAGON_TIGER.accentColor,
                      fontSize: "1.1rem",
                    }}
                  >
                    <FaDragon />
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold">Dragon Tiger</h6>
                    <small className="text-muted">
                      Round #{dtRound?.roundNumber || "N/A"}
                    </small>
                  </div>
                </div>
                <span
                  className={`badge ${getStatusBadgeColor(dtRound?.status)}`}
                >
                  {dtRound?.status || "UNKNOWN"}
                </span>
              </div>
              <div className="card-body">
                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <div className="text-center p-3 bg-light rounded-3">
                      <h6 className="text-muted small mb-1">Players</h6>
                      <h4 className="fw-bold mb-0">
                        {dtRound?.totalPlayers || 0}
                      </h4>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-3 bg-light rounded-3">
                      <h6 className="text-muted small mb-1">Total Bets</h6>
                      <h4 className="fw-bold mb-0">
                        {formatCurrency(
                          (dtRound?.totalDragonAmount || 0) +
                            (dtRound?.totalTigerAmount || 0) +
                            (dtRound?.totalTieAmount || 0),
                        )}
                      </h4>
                    </div>
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-4">
                    <div className="d-flex justify-content-between align-items-center p-2 bg-danger bg-opacity-10 rounded-3">
                      <span className="text-danger d-inline-flex align-items-center gap-2">
                        <FaDragon /> Dragon
                      </span>
                      <span className="fw-bold">
                        {formatCurrency(dtRound?.totalDragonAmount)}
                      </span>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="d-flex justify-content-between align-items-center p-2 bg-primary bg-opacity-10 rounded-3">
                      <span className="text-primary d-inline-flex align-items-center gap-2">
                        <FaCat /> Tiger
                      </span>
                      <span className="fw-bold">
                        {formatCurrency(dtRound?.totalTigerAmount)}
                      </span>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="d-flex justify-content-between align-items-center p-2 bg-warning bg-opacity-10 rounded-3">
                      <span className="text-warning d-inline-flex align-items-center gap-2">
                        <FaBalanceScale /> Tie
                      </span>
                      <span className="fw-bold">
                        {formatCurrency(dtRound?.totalTieAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Aviator */}
          <div className="col-xl-6 col-lg-6 col-12">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3"
                    style={{
                      width: 40,
                      height: 40,
                      background: GAME_META.AVIATOR.accentBg,
                      color: GAME_META.AVIATOR.accentColor,
                      fontSize: "1.1rem",
                    }}
                  >
                    <FaPlane />
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold">Aviator</h6>
                    <small className="text-muted">
                      Round #{avRound?.roundNumber || "N/A"}
                    </small>
                  </div>
                </div>
                <span
                  className={`badge ${getStatusBadgeColor(avRound?.status)}`}
                >
                  {avRound?.status || "UNKNOWN"}
                </span>
              </div>
              <div className="card-body">
                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <div className="text-center p-3 bg-light rounded-3">
                      <h6 className="text-muted small mb-1">Players</h6>
                      <h4 className="fw-bold mb-0">
                        {avRound?.totalPlayers || 0}
                      </h4>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-3 bg-light rounded-3">
                      <h6 className="text-muted small mb-1">Total Bets</h6>
                      <h4 className="fw-bold mb-0">
                        {formatCurrency(avRound?.totalBetAmount)}
                      </h4>
                    </div>
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <div className="d-flex justify-content-between align-items-center p-2 bg-info bg-opacity-10 rounded-3">
                      <span className="text-info d-inline-flex align-items-center gap-2">
                        <FaPlane /> Multiplier
                      </span>
                      <span
                        className="fw-bold text-info"
                        style={{
                          fontSize: "1.1rem",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {avRound?.currentMultiplier
                          ? `${avRound.currentMultiplier}x`
                          : "—"}
                      </span>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex justify-content-between align-items-center p-2 bg-warning bg-opacity-10 rounded-3">
                      <span className="text-warning d-inline-flex align-items-center gap-2">
                        🎯 Crash Point
                      </span>
                      <span className="fw-bold text-warning">
                        {avRound?.crashPoint ? `${avRound.crashPoint}x` : "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===================== TODAY'S BETTING STATS ===================== */}
        <div className="mb-3">
          <h5 className="fw-bold mb-0">
            <FiActivity className="me-2 text-primary" />
            Today's Betting Stats
          </h5>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-xl-4 col-lg-4 col-12">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                <h6 className="card-title mb-0 fw-bold">Combined</h6>
                <span className="badge bg-primary bg-opacity-10 text-primary">
                  {bet.combined?.todayBets || 0} bets
                </span>
              </div>
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-6">
                    <div className="text-center p-2 bg-light rounded-3">
                      <div className="text-muted small">Collection</div>
                      <div className="fw-bold text-success">
                        {formatCurrency(bet.combined?.todayCollection)}
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-2 bg-light rounded-3">
                      <div className="text-muted small">Payout</div>
                      <div className="fw-bold text-danger">
                        {formatCurrency(bet.combined?.todayPayout)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-center p-3 bg-light rounded-3">
                  <div className="text-muted small">Profit</div>
                  <h4
                    className={`fw-bold mb-0 ${(bet.combined?.todayProfit || 0) >= 0 ? "text-success" : "text-danger"}`}
                  >
                    {formatCurrency(bet.combined?.todayProfit)}
                  </h4>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-4 col-lg-4 col-12">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                <h6 className="card-title mb-0 fw-bold d-flex align-items-center gap-2">
                  <FaDragon className="text-danger" /> Dragon Tiger
                </h6>
                <span className="badge bg-danger bg-opacity-10 text-danger">
                  {bet.dragonTiger?.todayBets || 0} bets
                </span>
              </div>
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-6">
                    <div className="text-center p-2 bg-light rounded-3">
                      <div className="text-muted small">Collection</div>
                      <div className="fw-bold text-success">
                        {formatCurrency(bet.dragonTiger?.todayCollection)}
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-2 bg-light rounded-3">
                      <div className="text-muted small">Payout</div>
                      <div className="fw-bold text-danger">
                        {formatCurrency(bet.dragonTiger?.todayPayout)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-center p-3 bg-light rounded-3">
                  <div className="text-muted small">Profit</div>
                  <h4
                    className={`fw-bold mb-0 ${(bet.dragonTiger?.todayProfit || 0) >= 0 ? "text-success" : "text-danger"}`}
                  >
                    {formatCurrency(bet.dragonTiger?.todayProfit)}
                  </h4>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-4 col-lg-4 col-12">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                <h6 className="card-title mb-0 fw-bold d-flex align-items-center gap-2">
                  <FaPlane className="text-info" /> Aviator
                </h6>
                <span className="badge bg-info bg-opacity-10 text-info">
                  {bet.aviator?.todayBets || 0} bets
                </span>
              </div>
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-6">
                    <div className="text-center p-2 bg-light rounded-3">
                      <div className="text-muted small">Collection</div>
                      <div className="fw-bold text-success">
                        {formatCurrency(bet.aviator?.todayCollection)}
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-2 bg-light rounded-3">
                      <div className="text-muted small">Payout</div>
                      <div className="fw-bold text-danger">
                        {formatCurrency(bet.aviator?.todayPayout)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-center p-3 bg-light rounded-3">
                  <div className="text-muted small">Profit</div>
                  <h4
                    className={`fw-bold mb-0 ${(bet.aviator?.todayProfit || 0) >= 0 ? "text-success" : "text-danger"}`}
                  >
                    {formatCurrency(bet.aviator?.todayProfit)}
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===================== RECENT BETS ===================== */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="card-title mb-0 fw-bold">
                  <FiList className="me-2 text-primary" /> Recent Bets
                </h5>
                <div className="d-flex gap-2">
                  <button
                    className={`btn btn-sm ${activeBetTab === "DRAGON_TIGER" ? "btn-danger" : "btn-outline-danger"}`}
                    onClick={() => setActiveBetTab("DRAGON_TIGER")}
                    style={{ fontWeight: 600 }}
                  >
                    <FaDragon className="me-1" /> Dragon Tiger ({dtTotal})
                  </button>
                  <button
                    className={`btn btn-sm ${activeBetTab === "AVIATOR" ? "btn-info text-white" : "btn-outline-info"}`}
                    onClick={() => setActiveBetTab("AVIATOR")}
                    style={{ fontWeight: 600 }}
                  >
                    <FaPlane className="me-1" /> Aviator ({avTotal})
                  </button>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  {activeBetTab === "DRAGON_TIGER" ? (
                    <table className="table table-hover align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th>Round</th>
                          <th>Player</th>
                          <th>Side</th>
                          <th>Amount</th>
                          <th>Result</th>
                          <th>P/L</th>
                          <th>Status</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dtBets.length > 0 ? (
                          dtBets.map((b) => (
                            <tr key={b._id}>
                              <td>
                                <span className="text-danger fw-bold">
                                  #{b.round?.roundNumber || "N/A"}
                                </span>
                              </td>
                              <td>
                                {b.user?.mobile || b.user?.name || "Guest"}
                              </td>
                              <td>
                                <span
                                  className={`badge ${getSelectionBadgeColor(b.selection)}`}
                                >
                                  {b.selection || "N/A"}
                                </span>
                              </td>
                              <td>{formatCurrency(b.amount)}</td>
                              <td>
                                <span
                                  className={`badge ${getStatusBadgeColor(b.result)}`}
                                >
                                  {b.result || "—"}
                                </span>
                              </td>
                              <td
                                className={
                                  (b.profit || 0) >= 0
                                    ? "text-success fw-bold"
                                    : "text-danger fw-bold"
                                }
                              >
                                {(b.profit || 0) >= 0 ? "+" : ""}
                                {formatCurrency(b.profit || 0)}
                              </td>
                              <td>
                                <span
                                  className={`badge ${getStatusBadgeColor(b.status)}`}
                                >
                                  {b.status || "PENDING"}
                                </span>
                              </td>
                              <td className="small text-muted">
                                {formatDate(b.createdAt)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              No Dragon Tiger bets available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <table className="table table-hover align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th>Round</th>
                          <th>Player</th>
                          <th>Bet Type</th>
                          <th>Amount</th>
                          <th>Multiplier</th>
                          <th>Win</th>
                          <th>Status</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {avBets.length > 0 ? (
                          avBets.map((b) => (
                            <tr key={b._id}>
                              <td>
                                <span className="text-info fw-bold">
                                  #{b.round?.roundNumber || "N/A"}
                                </span>
                              </td>
                              <td>
                                {b.user?.mobile || b.user?.name || "Guest"}
                              </td>
                              <td>
                                <span className="badge bg-secondary">
                                  {b.betType || "—"}
                                </span>
                              </td>
                              <td>{formatCurrency(b.amount)}</td>
                              <td>
                                {b.cashedOut && b.cashoutMultiplier ? (
                                  <span className="text-success fw-bold">
                                    {b.cashoutMultiplier}x
                                  </span>
                                ) : b.round?.crashPoint ? (
                                  <span className="text-danger">
                                    Crashed @ {b.round.crashPoint}x
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td
                                className={
                                  (b.winAmount || 0) > 0
                                    ? "text-success fw-bold"
                                    : "text-muted"
                                }
                              >
                                {formatCurrency(b.winAmount || 0)}
                              </td>
                              <td>
                                <span
                                  className={`badge ${getStatusBadgeColor(b.status)}`}
                                >
                                  {b.status || "PENDING"}
                                </span>
                              </td>
                              <td className="small text-muted">
                                {formatDate(b.createdAt)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              No Aviator bets available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* ✅ PAGINATION — DT */}
                {activeBetTab === "DRAGON_TIGER" && dtTotalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                    <div className="text-muted small">
                      Page {dtPage} of {dtTotalPages} · showing {dtBets.length}{" "}
                      of {dtTotal} bets
                    </div>
                    <nav>
                      <ul className="pagination mb-0">
                        <li
                          className={`page-item ${dtPage === 1 ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setDtPage((p) => Math.max(1, p - 1))}
                            disabled={dtPage === 1}
                          >
                            <FiChevronLeft />
                          </button>
                        </li>

                        {/* Page numbers */}
                        {Array.from({ length: dtTotalPages }, (_, i) => i + 1)
                          .filter((p) => {
                            if (dtTotalPages <= 7) return true;
                            if (p === 1 || p === dtTotalPages) return true;
                            if (Math.abs(p - dtPage) <= 1) return true;
                            return false;
                          })
                          .map((p, idx, arr) => {
                            const prev = arr[idx - 1];
                            const showEllipsis = prev && p - prev > 1;
                            return (
                              <React.Fragment key={p}>
                                {showEllipsis && (
                                  <li className="page-item disabled">
                                    <span className="page-link">…</span>
                                  </li>
                                )}
                                <li
                                  className={`page-item ${p === dtPage ? "active" : ""}`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setDtPage(p)}
                                  >
                                    {p}
                                  </button>
                                </li>
                              </React.Fragment>
                            );
                          })}

                        <li
                          className={`page-item ${dtPage === dtTotalPages ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() =>
                              setDtPage((p) => Math.min(dtTotalPages, p + 1))
                            }
                            disabled={dtPage === dtTotalPages}
                          >
                            <FiChevronRight />
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}

                {/* ✅ PAGINATION — Aviator */}
                {activeBetTab === "AVIATOR" && avTotalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                    <div className="text-muted small">
                      Page {avPage} of {avTotalPages} · showing {avBets.length}{" "}
                      of {avTotal} bets
                    </div>
                    <nav>
                      <ul className="pagination mb-0">
                        <li
                          className={`page-item ${avPage === 1 ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setAvPage((p) => Math.max(1, p - 1))}
                            disabled={avPage === 1}
                          >
                            <FiChevronLeft />
                          </button>
                        </li>

                        {Array.from({ length: avTotalPages }, (_, i) => i + 1)
                          .filter((p) => {
                            if (avTotalPages <= 7) return true;
                            if (p === 1 || p === avTotalPages) return true;
                            if (Math.abs(p - avPage) <= 1) return true;
                            return false;
                          })
                          .map((p, idx, arr) => {
                            const prev = arr[idx - 1];
                            const showEllipsis = prev && p - prev > 1;
                            return (
                              <React.Fragment key={p}>
                                {showEllipsis && (
                                  <li className="page-item disabled">
                                    <span className="page-link">…</span>
                                  </li>
                                )}
                                <li
                                  className={`page-item ${p === avPage ? "active" : ""}`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setAvPage(p)}
                                  >
                                    {p}
                                  </button>
                                </li>
                              </React.Fragment>
                            );
                          })}

                        <li
                          className={`page-item ${avPage === avTotalPages ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() =>
                              setAvPage((p) => Math.min(avTotalPages, p + 1))
                            }
                            disabled={avPage === avTotalPages}
                          >
                            <FiChevronRight />
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .hover-shadow {
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
        }
        .hover-shadow:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1) !important;
        }
        .bg-gradient-primary {
          background: linear-gradient(135deg, #4f46e5 0%, #312e81 100%);
        }
        .transition {
          transition: all 0.3s ease;
        }
        .icon-box {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .icon-box.sm {
          width: 40px;
          height: 40px;
          font-size: 23px;
        }
        .icon-box.md {
          width: 56px;
          height: 56px;
        }
        .pagination .page-item.active .page-link {
          background-color: #4f46e5;
          border-color: #4f46e5;
          color: #fff;
        }
        .pagination .page-link {
          color: #475467;
        }
        .pagination .page-link:hover {
          background-color: #eef2ff;
          border-color: #c7d2fe;
          color: #4f46e5;
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .live-dot {
          animation: livePulse 1.5s ease-in-out infinite;
        }
        @keyframes livePulse {
          0%,
          100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.4;
            transform: scale(1.3);
          }
        }
      `}</style>
    </>
  );
};

export default Dashboard;